import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Loader2, AlertCircle, Rocket, Upload, Paperclip, X } from 'lucide-react';
import { step4Schema, type Step4Data } from '@/lib/validations';
import { ACCEPT_ATTR, MAX_FILES, validateFiles } from '@/lib/uploads';

const geographicOptions = [
  'National (entire US)',
  'Multi-State / Regional',
  'Single State',
  'Metro Area / DMA',
  'City / Local',
  'Specific ZIP Codes / Neighborhoods',
  'Multiple regions — will discuss',
  'Haven\'t decided yet',
];

const budgetOptions = [
  'Under $5,000 / month',
  '$5,000 - $10,000 / month',
  '$10,000 - $25,000 / month',
  '$25,000 - $50,000 / month',
  '$50,000 - $100,000 / month',
  '$100,000+ / month',
  'Project-based (not monthly)',
  'Will discuss',
];

const timelineOptions = [
  'Ready to start immediately',
  'Within 30 days',
  'Within 60-90 days',
  'Planning for next quarter',
  'Just exploring options',
  'Will discuss',
];

interface Step4DetailsProps {
  initialData?: Partial<Step4Data>;
  onSubmit: (data: Step4Data, files: File[]) => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
}

export function Step4Details({ initialData, onSubmit, onBack, isSubmitting }: Step4DetailsProps) {
  const [formData, setFormData] = useState<Step4Data>({
    geographic_focus: initialData?.geographic_focus || '',
    target_audience: initialData?.target_audience || '',
    budget_range: initialData?.budget_range || '',
    timeline: initialData?.timeline || '',
    additional_notes: initialData?.additional_notes || '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof Step4Data, string>>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const merged = [...files, ...Array.from(list)];
    const err = validateFiles(merged);
    if (err) {
      setFileError(err);
    } else {
      setFileError(undefined);
      setFiles(merged);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFileError(undefined);
  };

  const handleChange = (name: keyof Step4Data, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = step4Schema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof Step4Data, string>> = {};
      result.error.errors.forEach((error) => {
        const field = error.path[0] as keyof Step4Data;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const fileErr = validateFiles(files);
    if (fileErr) {
      setFileError(fileErr);
      return;
    }

    await onSubmit(result.data, files);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-foreground text-sm font-medium">
          Geographic Focus *
        </Label>
        <Select
          value={formData.geographic_focus}
          onValueChange={(value) => handleChange('geographic_focus', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.geographic_focus ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {geographicOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.geographic_focus && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.geographic_focus}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="target_audience" className="text-foreground text-sm font-medium">
          Target Audience Description *
        </Label>
        <Textarea
          id="target_audience"
          value={formData.target_audience}
          onChange={(e) => handleChange('target_audience', e.target.value)}
          placeholder="Describe who you're trying to reach (demographics, interests, behaviors, etc.) or type 'Will discuss'"
          rows={3}
          className={`mt-1.5 resize-none ${errors.target_audience ? 'border-destructive' : ''}`}
        />
        <p className="text-xs text-muted-foreground mt-1">
          Example: "Adults 35-65 in Florida interested in outdoor recreation and home improvement"
        </p>
        {errors.target_audience && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.target_audience}
          </p>
        )}
      </div>

      <div>
        <Label className="text-foreground text-sm font-medium">
          Estimated Monthly Budget *
        </Label>
        <Select
          value={formData.budget_range}
          onValueChange={(value) => handleChange('budget_range', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.budget_range ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {budgetOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.budget_range && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.budget_range}
          </p>
        )}
      </div>

      <div>
        <Label className="text-foreground text-sm font-medium">
          Timeline / Urgency *
        </Label>
        <Select
          value={formData.timeline}
          onValueChange={(value) => handleChange('timeline', value)}
        >
          <SelectTrigger className={`mt-1.5 ${errors.timeline ? 'border-destructive' : ''}`}>
            <SelectValue placeholder="Select one..." />
          </SelectTrigger>
          <SelectContent>
            {timelineOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.timeline && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {errors.timeline}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="additional_notes" className="text-foreground text-sm font-medium">
          Anything Else We Should Know? <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <Textarea
          id="additional_notes"
          value={formData.additional_notes}
          onChange={(e) => handleChange('additional_notes', e.target.value)}
          placeholder="Share any additional context, questions, or requirements..."
          rows={3}
          className="mt-1.5 resize-none"
        />
      </div>

      <div>
        <Label className="text-foreground text-sm font-medium">
          Supporting Files <span className="text-muted-foreground font-normal">(optional)</span>
        </Label>
        <p className="text-xs text-muted-foreground mt-1 mb-2">
          RFPs, brand guidelines, or creative samples — PDF, Word, PowerPoint, or images. Up to 15MB each, {MAX_FILES} files max.
        </p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="sr-only"
          onChange={(e) => addFiles(e.target.files)}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 rounded-md border border-dashed border-input bg-background hover:bg-muted/50 transition-colors py-3 text-sm text-muted-foreground focus-ring"
        >
          <Upload className="w-4 h-4" />
          Choose files
        </button>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1.5">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-sm bg-muted/40 rounded-md px-3 py-2">
                <Paperclip className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-xs text-muted-foreground flex-shrink-0">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-muted-foreground hover:text-destructive flex-shrink-0"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {fileError && (
          <p className="text-destructive text-xs mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {fileError}
          </p>
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 bg-gold hover:bg-gold-light text-dark-bg font-semibold"
        >
          {isSubmitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Submit & Get Started
              <Rocket className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
